import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Download, Phone, Clock, Euro, ListChecks, MoreHorizontal, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, fmtMoney, downloadFile } from "@/lib/api";
import { inputClass } from "@/components/FieldInput";
import { Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ActivityDetailDialog, ActivityFormDialog, TypeBadge } from "@/components/activities/ActivityDialogs";
import { ACTIVITY_TYPES, fmtHours } from "@/components/activities/types";

const EMPTY_FILTERS = { q: "", type: "all", from: "", to: "" };
const num = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

function Kpi({ icon: I, label, value, testId }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><I className="h-4 w-4 text-slate-400" /></div>
      <p className="font-heading text-3xl font-bold text-slate-900 mt-3" data-testid={testId}>{value}</p>
    </Card>
  );
}

export function ActivitiesPanel({ trustId }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const params = { q: filters.q || undefined, type: filters.type !== "all" ? filters.type : undefined, from: filters.from || undefined, to: filters.to || undefined };
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/trusts/${trustId}/activities`, { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
  }, [trustId, filters.q, filters.type, filters.from, filters.to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const rows = data?.items || [];
  const s = data?.summary || { count: 0, hours: 0, calls: 0, cost: 0 };
  const canWrite = !!data?.can_write;

  const save = async (f) => {
    if (!f.date || !f.title?.trim() || f.hours === "" || f.hours === null) return toast.error("Compila data, titolo e durata");
    setSaving(true);
    try {
      const payload = { date: f.date, type: f.type, title: f.title.trim(), description: f.description || null, hours: Number(f.hours), cost: f.cost === "" || f.cost === null || f.cost === undefined ? null : Number(f.cost) };
      if (f.id) await api.put(`/activities/${f.id}`, payload);
      else await api.post(`/trusts/${trustId}/activities`, payload);
      toast.success(f.id ? "Attività aggiornata" : "Attività registrata");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/activities/${toDelete.id}`);
      toast.success("Attività eliminata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };
  const pdf = () => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    downloadFile(`/trusts/${trustId}/activities/pdf${qs ? `?${qs}` : ""}`, "Registro_attivita.pdf").catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div data-testid="activities-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <Kpi icon={ListChecks} label="Attività visualizzate" value={s.count} testId="activities-kpi-count" />
        <Kpi icon={Clock} label="Durata totale" value={fmtHours(s.hours)} testId="activities-kpi-hours" />
        <Kpi icon={Phone} label="Telefonate" value={s.calls} testId="activities-kpi-calls" />
        <Kpi icon={Euro} label="Costi informativi" value={fmtMoney(s.cost)} testId="activities-kpi-cost" />
      </div>

      <Card>
        <div className="p-4 border-b border-slate-100 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input data-testid="activities-search-input" className={`${inputClass} pl-9`} placeholder="Cerca per titolo o nota…" value={filters.q} onChange={set("q")} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="activities-pdf-button" onClick={pdf}><Download className="h-4 w-4 mr-1" /> Scarica PDF</Button>
              {canWrite && (
                <Button size="sm" data-testid="activities-new-button" className="bg-[#0A2540] hover:bg-[#031B33] text-white" onClick={() => setForm({})}><Plus className="h-4 w-4 mr-1" /> Nuova attività</Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3" data-testid="activities-filters">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipologia</label>
              <select data-testid="activities-filter-type" className={`${inputClass} w-44`} value={filters.type} onChange={set("type")}>
                <option value="all">Tutte</option>{Object.entries(ACTIVITY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Dal</label><input type="date" data-testid="activities-filter-from" className={`${inputClass} w-40`} value={filters.from} onChange={set("from")} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Al</label><input type="date" data-testid="activities-filter-to" className={`${inputClass} w-40`} value={filters.to} onChange={set("to")} /></div>
            <Button variant="ghost" size="sm" data-testid="activities-filter-reset" className="text-slate-600" onClick={() => setFilters(EMPTY_FILTERS)}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Azzera filtri</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="activities-table">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Data</th><th className="px-4 py-3 font-semibold">Tipologia</th><th className="px-4 py-3 font-semibold">Attività</th>
                <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Durata (ore)</th><th className="px-4 py-3 font-semibold text-right">Costi (€)</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Autore / ultima modifica</th>{canWrite && <th className="px-4 py-3 text-right font-semibold">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>}
              {data && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="activities-empty">Nessuna attività registrata</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`activities-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors align-top">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3"><TypeBadge type={r.type} /></td>
                  <td className="px-4 py-3 max-w-[360px]">
                    <button type="button" className="font-medium text-slate-900 hover:text-sky-700 hover:underline text-left" data-testid={`activities-title-${r.id}`} onClick={() => setDetail(r)}>{r.title}</button>
                    {r.description && <p className="text-xs text-slate-500 truncate">{r.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{num(r.hours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{r.cost != null ? fmtMoney(r.cost) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                    <p>{r.author_name}</p>
                    <p className="text-slate-400">{r.updated_at ? `mod. ${r.updated_by_name} · ${fmtDateTime(r.updated_at)}` : fmtDateTime(r.created_at)}</p>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`activities-actions-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem data-testid={`activities-edit-${r.id}`} onClick={() => setForm(r)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`activities-delete-${r.id}`} className="text-red-600 focus:text-red-700" onClick={() => setToDelete(r)}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="bg-slate-50 font-semibold text-sm" data-testid="activities-totals">
                <td className="px-4 py-3" colSpan={3}>Totali ({s.count} attività)</td>
                <td className="px-4 py-3 text-right font-mono">{num(s.hours)}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtMoney(s.cost)}</td><td colSpan={canWrite ? 2 : 1} />
              </tr></tfoot>
            )}
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100" data-testid="activities-cost-note">I costi sono informativi e non generano movimenti contabili.</p>
      </Card>

      <ActivityDetailDialog item={detail} onClose={() => setDetail(null)} />
      <ActivityFormDialog item={form} onClose={() => setForm(null)} onSave={save} saving={saving} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="activities-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'attività?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” verrà rimossa definitivamente. L'operazione e il contenuto eliminato restano registrati nel registro attività.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activities-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="activities-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
